/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import { HtmlHeadInclude, CultureInfoFormatter } from '@msdyn365-commerce/core';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Collapse,  Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IHeaderResources } from '../definition-extensions/header.ext.props.autogenerated';

const headerView: React.FC<IHeaderViewProps> = props => {
    const {
        context,
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        Divider
    } = props;

    React.useEffect(() => {
        //@ts-ignore
        if (context?.cultureFormatter && context.cultureFormatter.locale !== "en-US") {
            context.cultureFormatter = new CultureInfoFormatter(
                "en-US",
                context.actionContext?.requestContext?.channel?.Currency ?? "SAR",
                context.actionContext.requestContext.query
            )
        }
    },[]);

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <HtmlHeadInclude>
                    <meta name='Petal-Search-site-verification' content='b1a9265943' />
                </HtmlHeadInclude>
                <Node {...HeaderTopBarContainer}>
                    {_renderNavigateBack(props)}
                    {props.navIcon}
                    {props.logo}
                    {_renderReactFragment(props.search)}
                    {props.preferredStore}
                    {_renderAccountBlock(props, false)}
                    {props.wishListIconDesktop}
                    <Node {...Divider} />
                    {props.cartIcon}
                    {_renderReactFragment(props.siteOptions)}
                </Node>
                {_renderCollapseMenu(props)}
                <Node className='ms-header__desktop-view'>
                    {_renderReactFragment(props.menuBar)}
                </Node>
            </Node>
        </Module>
    );
};

function _renderCollapseMenu(props: IHeaderViewProps): JSX.Element | null {
    const { Divider, /*MobileMenuLinksContainer,*/ mobileMenuCollapsed } = props;
    return (
        <Collapse className='ms-header__collapsible-hamburger' isOpen={!mobileMenuCollapsed}>
            {/*<Node {...MobileMenuLinksContainer}>
                {_renderReactFragment(props.search)}
                {_renderMobileAccountBlock(props, true)}
                {props.wishListIconMobile}
                {props.siteOptions}
            </Node>*/}
            <Node {...Divider} />
            {_renderReactFragment(props.menuBar)}
        </Collapse>
    );
}

/*
const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return (
        <>
            {customer ? customer.FirstName || customer.Name : ''}
        </>
    );
};
*/

/*
function _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        signOutLink,
        signInLink,
        data,
        accountLinks } = props;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames('ms-header__drawer', AccountInfoDropdownParentContainer.className, renderForMobile ? 'account-mobile' : 'account-desktop');

        if (accountLinks) {
            return (
                <Drawer
                    className={accountClassName} openGlyph='ms-header__drawer-open' closeGlyph='ms-header__drawer-close'
                    glyphPlacement='end' toggleButtonText={renderCustomerName(data.accountInformation)}>
                    <div>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }
    return null;
}
*/

function _renderAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(AccountInfoDropdownParentContainer.className, renderForMobile ? 'account-mobile' : 'account-desktop', 'trus-accountbutton');
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }

    return null;
}


function _renderNavigateBack(props: IHeaderViewProps): JSX.Element | null {
    const resources = props.resources as IHeaderResources;

    return (
        <button className='trus-header__navigate-back_button msc-btn' onClick={() => history.back()}>
            <span className='trus-header__navigate-back_button_text'>{resources.backButtonAriaLabel}</span>
        </button>
    );
}


function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0 ? items.map((slot: React.ReactNode, index: number) => {
                return (<React.Fragment key={index}>
                    {slot}
                </React.Fragment>);
            }) : null}
        </>
    );
}

export default headerView;
